import { AdvancedSelect, SelectionValue } from '../../widgets/select';
import { Dialog, useDialogManager } from '../../widgets/dialog';
import { FormControl } from '../../widgets/formControl';
import { Input } from '../../widgets/input';
import { PrimaryButton } from '../../widgets/buttons';
import { removeNotification, showNotification } from '../../widgets/notificationToaster';
import React, { FC, Suspense, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { useNavigate } from 'react-router-dom';
import config from 'src/shared/config';
import { backofficeUrl, T, toGlobalID, toLocalID } from 'src/shared/helpers';
import { useValidationContext, ValidationStateProvider } from 'src/shared/validation';
import { AddSalesChannelsDialogWindow, ApiLabel, ButtonContainer, DoubleHr } from '../../../shared/connectDialogStyle';
import { Loader } from '../../../shared/docsDialog';
import { EcommerceId } from '../../../shared/enums';
import { ValidationBox } from '../../widgets/validation/validationBox';
import ApiKeys from '../myAccount/apiKeys';
import {
    apiIntegrationSalesChannelDialogInsertIntegrationMutation,
    apiIntegrationSalesChannelDialogInsertIntegrationMutation$data
} from './__generated__/apiIntegrationSalesChannelDialogInsertIntegrationMutation.graphql';
import {
    apiIntegrationSalesChannelDialogInsertMutation,
    apiIntegrationSalesChannelDialogInsertMutation$data
} from './__generated__/apiIntegrationSalesChannelDialogInsertMutation.graphql';
import { apiIntegrationSalesChannelDialogQuery } from './__generated__/apiIntegrationSalesChannelDialogQuery.graphql';
import { CurrenciesNote } from './blankApiIntegrationSalesChannelDialog';
import { checkUrl } from './helpers';
import Stack from '../../widgets/stack/stack';

interface SalesChannelApiProps {
    salesChannelID: string;
    hasMasterIntegration?: boolean;
    title?: string;
    zendeskDocId?: string | undefined;
    urlToRedirect?: string | undefined;
    onSalesChannelCreated?: () => void;
    onSalesChannelUpdated?: () => void;
}

interface Currency {
    label: string;
    value: string;
}

const salesChannelinfo = graphql`
    query apiIntegrationSalesChannelDialogQuery($salesChannelID: ID) {
        currencies {
            id
            code
            name
            symbol
        }
        ecommerceSettingsForApiIntegration(salesChannelID: $salesChannelID) {
            currencyID
            remoteUrl
            productsApi
        }
        lcidCurrenciesList {
            languageCulture
        }
    }
`;

const saveApiSalesChannels = graphql`
    mutation apiIntegrationSalesChannelDialogInsertMutation($input: ApiSalesChannelInput!) {
        upsertApiSalesChannel(input: $input) {
            id
            clientID
            secretKey
            salesChannelID
        }
    }
`;
const saveApiIntegration = graphql`
    mutation apiIntegrationSalesChannelDialogInsertIntegrationMutation($input: EcommerceSettingsInput!) {
        saveEcommerceSettings(settingsInput: $input) {
            ecommerceID
        }
    }
`;

const ApiIntegrationSalesChannelDialog: FC<SalesChannelApiProps> = (salesChannel) => {
    const validationContext = useValidationContext();
    const query = useLazyLoadQuery<apiIntegrationSalesChannelDialogQuery>(salesChannelinfo, { salesChannelID: salesChannel.salesChannelID });
    // const isForEdit = salesChannel.salesChannelID !== undefined;
    const currenciesList: Currency[] =
        query?.currencies?.map((currency) => {
            return { label: `${currency!.name!} (${currency!.code} ${currency!.symbol})`, value: currency!.id! };
        }) ?? [];

    const defaultCurrency = query?.currencies?.find((currency) => currency?.code === 'USD');
    const storeCurrency = query.ecommerceSettingsForApiIntegration?.currencyID
        ? toGlobalID(query?.ecommerceSettingsForApiIntegration?.currencyID ?? '', 'Currency')
        : undefined;

    const [selectedCurrency, setSelectedCurrency] = React.useState<string>(storeCurrency || defaultCurrency?.id || '');

    const [storeUrl, setStoreUrl] = React.useState<string>(query.ecommerceSettingsForApiIntegration?.remoteUrl || '');
    const [productApiUrl, setProductApiUrl] = React.useState<string>(query.ecommerceSettingsForApiIntegration?.productsApi || '');
    const [recaptchaToken, setRecaptchaToken] = useState('');

    const { showDialog, closeDialog } = useDialogManager();
    const navigate = useNavigate();

    const [insertMutation, isInserting] = useMutation<apiIntegrationSalesChannelDialogInsertMutation>(saveApiSalesChannels);
    const [saveIntegration, isSavingIntegration] = useMutation<apiIntegrationSalesChannelDialogInsertIntegrationMutation>(saveApiIntegration);

    const handleSaveClick = async () => {
        if (!validationContext.submit()) return;

        if (!checkUrl(storeUrl, true) && !checkUrl(productApiUrl, false, true)) {
            const loadingNotification = showNotification({
                type: 'default',
                message: T._(`Saving sales channel... this may take a while don't exit the page!`, 'Admin')
            });

            try {
                if (!isInserting && !isSavingIntegration) {
                    if (!salesChannel.hasMasterIntegration || toLocalID(salesChannel.salesChannelID) === 1) {
                        const isMasterParam = toLocalID(salesChannel.salesChannelID) === 1 ? 'isMaster=true&' : '';
                        const returnUrlAfterSavingIntegration = encodeURI(backofficeUrl(`/sales-channels?added=true&${isMasterParam}ecommerceType=API`));
                        await new Promise<apiIntegrationSalesChannelDialogInsertIntegrationMutation$data>((resolve, reject) => {
                            saveIntegration({
                                updater: (store) => store.invalidateStore(),
                                variables: {
                                    input: {
                                        currencyGlobalID: selectedCurrency,
                                        ecommerceGlobalID: toGlobalID(EcommerceId.API, 'ECommerce'),
                                        salesChannelID: salesChannel.salesChannelID,
                                        recaptchaToken: recaptchaToken,
                                        productsApi: productApiUrl,
                                        remoteUrl: storeUrl
                                    }
                                },
                                onCompleted: (data) => {
                                    resolve(data);
                                    closeDialog('api-connection');
                                    removeNotification(loadingNotification);
                                    window.location.href = backofficeUrl(`/subscription/checkForExisting?returnUrl=${returnUrlAfterSavingIntegration}`);
                                },
                                onError: reject
                            });
                        });

                        if (salesChannel.onSalesChannelCreated) {
                            salesChannel.onSalesChannelCreated();
                        }
                    } else {
                        await new Promise<apiIntegrationSalesChannelDialogInsertMutation$data>((resolve, reject) => {
                            insertMutation({
                                updater: (store) => store.invalidateStore(),
                                onCompleted: (data) => {
                                    resolve(data);
                                    closeDialog('api-connection');
                                },
                                onError: reject,
                                variables: {
                                    input: {
                                        id: salesChannel.salesChannelID || '',
                                        storeUrl: storeUrl,
                                        productApiUrl: productApiUrl,
                                        currencyID: selectedCurrency
                                    }
                                }
                            });
                        });

                        removeNotification(loadingNotification);
                        showNotification({
                            type: 'success',
                            message: T._('Sales channel edited', 'Admin'),
                            dismiss: {
                                duration: 5000
                            }
                        });

                        if (salesChannel.onSalesChannelUpdated) {
                            salesChannel.onSalesChannelUpdated();
                        }
                    }
                }
            } catch {
                showNotification({
                    type: 'danger',
                    message: T._('Error saving sales channel.', 'Admin'),
                    dismiss: {
                        duration: 5000
                    }
                });
            } finally {
                removeNotification(loadingNotification);
            }
        }
    };

    const handleSelectedCurrency = (item: SelectionValue<Currency>) => {
        if (item != null && !Array.isArray(item)) {
            setSelectedCurrency(item.value);
        }
    };

    return (
        <Dialog windowDecorator={AddSalesChannelsDialogWindow} size='large' onClose={() => closeDialog('api-connection')} title={salesChannel?.title ?? ''}>
            <Suspense fallback={Loader}>
                <div>
                    <ApiLabel
                        dangerouslySetInnerHTML={{
                            __html: `<strong><h2>Integration settings</h2></strong>`
                        }}
                    />
                    <ApiLabel
                        dangerouslySetInnerHTML={{
                            __html: `Start by entering the URL of your store and specifying its base currency. For more guidance, refer to the <a href='https://docs.zakeke.com/docs/API/Integration/Integrating-Zakeke' target='_blank' rel='noopener'>integration guide</a>.`
                        }}
                    />
                    <Stack>
                        <FormControl label={T._('URL of the Store to integrate*', 'Admin')}>
                            <Input
                                required={true}
                                value={storeUrl}
                                placeholder={'Insert your store URL'}
                                onChange={(e) => {
                                    setStoreUrl(e.target.value);
                                }}
                                validationKey='apiSalesChannelStoreUrl'
                                validationLabel={T._('URL of the Store to integrate', 'Admin')}
                                validate={(value) => !!checkUrl(value, true, false)}
                                validationMessage={(value) => T._(checkUrl(value, true, false), 'Admin')}
                            />
                        </FormControl>
                        <FormControl
                            label={T._('Store base currency*', 'Admin')}
                            tooltipLabelHtml={T._(
                                'Enter the currency used in your e-commerce store. Zakeke will use this as the base currency for transaction calculations and all other currency-related operations. Ensure this matches the currency set in your store to avoid discrepancies.',
                                'Admin'
                            )}
                        >
                            <AdvancedSelect
                                placeholder={T._('US Dollar', 'Admin')}
                                isMulti={false}
                                options={currenciesList}
                                value={currenciesList.find((option) => option.value == selectedCurrency)}
                                onChange={handleSelectedCurrency}
                                validationKey='apiSalesChannelCurrency'
                                validationLabel={T._('Store base currency', 'Admin')}
                                validate={(value) => !value}
                                validationMessage={T._('Currency is required.', 'Admin')}
                            />
                            {/* Uso lo split in quanto le culture potrebbero avere un dialetto particolare(es. "en-US"). In questo modo le lingue con hanno la
						traduzione vengono gestite in modo corretto */}
                            {!query.lcidCurrenciesList?.some((item) => item!.languageCulture?.split('-')[0] === config.culture.split('-')[0]) && (
                                <CurrenciesNote>
                                    {T._('Search for a currency by English name or ISO code (e.g., US Dollar or USD, Euro or EUR).', 'Admin')}
                                </CurrenciesNote>
                            )}
                        </FormControl>

                        <FormControl
                            label={T._('Product Catalog API', 'Admin')}
                            tooltipLabelHtml={
                                <p
                                    dangerouslySetInnerHTML={{
                                        __html: `
						  Enter your Product Catalog API URL to integrate your products with Zakeke. This API method allows for a more dynamic connection, enabling real-time syncing of product listings and inventory management. This approach serves as an alternative to the CSV method for enhanced efficiency. For detailed instructions, refer to our <a href='https://docs.zakeke.com/docs/API/Integration/Connecting-Product/Intro-connecting-products#3-how-to-connect-products' target='_blank' rel='noopener'>guide</a>.`
                                    }}
                                ></p>
                            }
                        >
                            <Input
                                required={true}
                                value={productApiUrl}
                                placeholder={'Insert your Product Catalog API'}
                                onChange={(e) => setProductApiUrl(e.currentTarget.value)}
                                validationKey='apiSalesChannelProductApiUrl'
                                validationLabel={T._('Product Catalog API', 'Admin')}
                                validate={(value) => !!checkUrl(value, false, true)}
                                validationMessage={(value) => T._(checkUrl(value, false, true), 'Admin')}
                            />
                        </FormControl>
                    </Stack>

                    {toLocalID(salesChannel.salesChannelID) === 1 && (
                        <FormControl>
                            <ValidationBox
                                validationKey='apiSalesChannelRecaptcha'
                                validationLabel={T._('ReCAPTCHA', 'Admin')}
                                validationValue={recaptchaToken}
                                validate={(value) => !value}
                                validationMessage={T._('ReCAPTCHA is required.', 'Admin')}
                            >
                                <ReCAPTCHA
                                    sitekey={config.googleRecaptchaSitekey}
                                    theme={'light'}
                                    size={'normal'}
                                    onChange={(e) => e && setRecaptchaToken(e)}
                                ></ReCAPTCHA>
                            </ValidationBox>
                        </FormControl>
                    )}

                    <ButtonContainer>
                        <PrimaryButton onClick={handleSaveClick} disabled={isInserting || isSavingIntegration}>
                            {isInserting || isSavingIntegration ? T._('Saving...', 'Admin') : T._('Save', 'Admin')}
                        </PrimaryButton>
                    </ButtonContainer>

                    <DoubleHr />
                    <ApiLabel
                        dangerouslySetInnerHTML={{
                            __html: `<strong><h2>API Keys</h2></strong>`
                        }}
                    />
                    <ApiKeys salesChannelID={salesChannel.salesChannelID} />
                </div>
            </Suspense>
        </Dialog>
    );
};

const ApiIntegrationSalesChannelDialogWithValidation: FC<SalesChannelApiProps> = (props) => (
    <ValidationStateProvider>
        <ApiIntegrationSalesChannelDialog {...props} />
    </ValidationStateProvider>
);

export default ApiIntegrationSalesChannelDialogWithValidation;
